import React, { useEffect, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { oauthAPI } from '../lib/api';

// The OAuth consent screen — where a remote MCP client (claude.ai and the like)
// sends the browser to get the user's approval.
//
// It lives in the React app rather than being server-rendered so it can reuse
// the existing session, including Google sign-in: a server-rendered form would
// have needed a password, locking out every Google-only account.
export default function OAuthConsent() {
  const location = useLocation();
  const token = useAuthStore(state => state.token);

  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [scopes, setScopes] = useState([]);

  const params = Object.fromEntries(new URLSearchParams(location.search));

  // Not signed in: remember where we were so login can come back here with the
  // OAuth parameters intact. Losing them would mean starting the flow again.
  useEffect(() => {
    if (!token) {
      sessionStorage.setItem('mc_oauth_return', location.pathname + location.search);
    }
  }, [token, location]);

  useEffect(() => {
    if (!token) return;
    oauthAPI.authorizationRequest(params)
      .then(r => {
        setInfo(r.data);
        setScopes(r.data.requested_scopes.map(s => s.scope));
      })
      .catch(err => setError(
        err.response?.data?.error_description || err.response?.data?.error || 'Could not read the authorization request'
      ))
      .finally(() => setLoading(false));
  }, [token]);

  if (!token) return <Navigate to="/login" replace />;

  async function decide(approve) {
    setSubmitting(true);
    setError('');
    try {
      const r = await oauthAPI.authorize({ ...params, approve, scopes });
      // Off-origin by definition, so a full navigation rather than a route change.
      window.location.href = r.data.redirect_to;
    } catch (err) {
      setError(err.response?.data?.error_description || err.response?.data?.error || 'Authorization failed');
      setSubmitting(false);
    }
  }

  function toggleScope(scope) {
    setScopes(prev => (prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]));
  }

  const shell = (children) => (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
      <div className="bg-[#111111] border border-[#2a2a2a] rounded-lg w-full max-w-md p-6">{children}</div>
    </div>
  );

  if (loading) return shell(<div className="text-gray-400 text-sm text-center py-8">Loading…</div>);

  if (error && !info) {
    return shell(
      <>
        <h1 className="text-white font-semibold mb-2">Authorization failed</h1>
        <p className="text-sm text-red-400 mb-4">{error}</p>
        <p className="text-xs text-gray-600">
          Close this window and try connecting again from the client. If it keeps failing, the client&rsquo;s
          registration may be out of date.
        </p>
      </>
    );
  }

  const isLive = scopes.includes('live:execute');

  return shell(
    <>
      <div className="flex items-center gap-3 mb-1">
        {info.client.logo_uri && (
          <img src={info.client.logo_uri} alt="" className="w-8 h-8 rounded" />
        )}
        <h1 className="text-white font-semibold">{info.client.name}</h1>
      </div>
      <p className="text-sm text-gray-400 mb-5">
        wants to access Mission Control as <span className="text-white">{info.user.email}</span>.
      </p>

      {info.workspaces.length > 0 && (
        <div className="mb-4">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            {info.workspaces.length === 1 ? 'Workspace' : 'Workspaces'}
          </div>
          <p className="text-sm text-gray-300">{info.workspaces.map(ws => ws.name).join(', ')}</p>
          <p className="text-[11px] text-gray-600 mt-1">
            The connection works in every workspace you belong to, with your role in each — including ones you
            join later. <span className="font-mono">live:execute</span> only applies where you are an admin.
          </p>
        </div>
      )}

      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">It will be able to</div>
      <div className="space-y-1 mb-4">
        {info.requested_scopes.map(s => (
          <label key={s.scope} className="flex items-start gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-[#0a0a0a]">
            <input
              type="checkbox"
              checked={scopes.includes(s.scope)}
              onChange={() => toggleScope(s.scope)}
              className="mt-0.5 accent-blue-600"
            />
            <span className="min-w-0">
              <span className="text-xs font-mono text-white">{s.scope}</span>
              <span className="block text-[11px] text-gray-500 leading-snug">{s.description}</span>
            </span>
          </label>
        ))}
      </div>

      {isLive && (
        <div className="mb-4 px-3 py-2 bg-amber-950/40 border border-amber-800 rounded">
          <p className="text-xs text-amber-300 leading-relaxed">
            <span className="font-semibold">live:execute</span> allows on-air actions — TAKE, vMix functions,
            Singular graphics. Each one still needs explicit confirmation at the time, but untick this
            if the connector has no business touching a live show.
          </p>
        </div>
      )}

      {info.refused_scopes.length > 0 && (
        <div className="mb-4">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Not granted</div>
          {info.refused_scopes.map(s => (
            <p key={s.scope} className="text-[11px] text-gray-600">
              <span className="font-mono text-gray-500">{s.scope}</span> — {s.reason}
            </p>
          ))}
        </div>
      )}

      {error && <p className="text-xs text-red-400 mb-3">{error}</p>}

      <div className="flex gap-2">
        <button
          onClick={() => decide(true)}
          disabled={submitting || scopes.length === 0}
          className="flex-1 px-3 py-2 bg-blue-700 hover:bg-blue-600 disabled:opacity-40 text-white text-sm font-medium rounded transition-colors"
        >
          {submitting ? 'Authorizing…' : 'Authorize'}
        </button>
        <button
          onClick={() => decide(false)}
          disabled={submitting}
          className="px-4 py-2 bg-[#1a1a1a] hover:bg-[#222] border border-[#2a2a2a] text-gray-300 text-sm rounded transition-colors"
        >
          Deny
        </button>
      </div>

      <p className="text-[11px] text-gray-600 mt-3 leading-relaxed">
        You can revoke this at any time in Workspace Settings. Only authorize clients you trust —
        this grants access to your shows and rundowns.
      </p>
    </>
  );
}
